import { Injectable } from '@angular/core';
import { filter, map, Observable, switchMap, take } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Credential, LocalCanalConfiguracion } from '@models/.';
import { environment } from '@environments/environment';
import { ResponseSuccess } from '@interfaces/response-success';
import { AuthenticationState } from '@store/reducers/authentication.reducer';
import { selectCredential } from '@store/selectors/authentication.selectors';
import { selectStoreChannelConfigurationList } from '@store/selectors';
import { transformDropdown } from '@core/helpers';
import { SelectMenuItem } from '@core/interfaces';
import { StoreChannelFilter } from './interfaces/store-channel.filter';

@Injectable({
  providedIn: 'root',
})
export class StoreChannelConfigurationService {
  credential$: Observable<Credential | null>;
  localChannels$: Observable<LocalCanalConfiguracion[] | null>;

  constructor(
    private http: HttpClient,
    private store: Store<AuthenticationState>
  ) {
    this.credential$ = this.store.select(selectCredential);
    this.localChannels$ = this.store.select(selectStoreChannelConfigurationList);
  }

  find(id: string): Observable<ResponseSuccess<LocalCanalConfiguracion>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseSuccess<LocalCanalConfiguracion>>(
          `${environment.microEnterprise}/api/v1/channel/${id}`,
          { headers: { Authorization: token } },
        );
      }),
      take(1),
    );
  }

  findByName(name: string): Observable<LocalCanalConfiguracion|undefined> {
    return this.localChannels$.pipe(
      filter((localChannels): localChannels is LocalCanalConfiguracion[] => localChannels !== null && localChannels.length > 0),
      map((localChannels) => localChannels.find((localChannel) => localChannel.nombre === name)),
      take(1),
    );
  }

  findAllByName(name?: string): Observable<LocalCanalConfiguracion[]> {
    return this.localChannels$.pipe(
      filter((localChannels): localChannels is LocalCanalConfiguracion[] => localChannels !== null && localChannels.length > 0),
      map((localChannels) => {
        return localChannels.filter((localChannel) => localChannel.nombre === name || !name);
      }),
    );
  }

  findAll({ localId, section }: StoreChannelFilter): Observable<ResponseSuccess<LocalCanalConfiguracion>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        const params = {
          ...(!!localId && { localId }),
          ...(!!section && { seccion: section }),
          // ...(!localId && { cuentaId: credential!.idCuenta! }),
        };

        return this.http.get<ResponseSuccess<LocalCanalConfiguracion>>(
          `${environment.microConfiguraciones}/api/v1/local-canal`,
          {
            headers: { Authorization: token },
            params: { ...params }
          },
        );
      }),
      take(1),
    );
  }

  getDropdown(): Observable<SelectMenuItem<undefined>[]> {
    return this.localChannels$.pipe(
      filter((localChannels): localChannels is LocalCanalConfiguracion[] => !!localChannels),
      map((localChannels) => transformDropdown(localChannels, 'idLocalCanal', 'nombre') as SelectMenuItem<undefined>[]),
      take(1),
    );
  }
}
